import { useCallback, useState } from 'react';
import {
  usePlaidLink,
  type PlaidLinkOnExit,
  type PlaidLinkOnSuccess,
} from 'react-plaid-link';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Landmark,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Unlink,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { Menu } from '@/components/menu';
import { Select } from '@/components/select';
import { SkeletonList } from '@/components/skeleton';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  useBankConnections,
  useCreateBankConnection,
  useCreateLinkToken,
  useDeleteBankConnection,
  usePostableAccounts,
  useSyncConnection,
  useUpdateBankAccount,
} from '@/lib/banking';
import { extractMessage } from '@/lib/form-error';
import type { BankAccountData, BankConnectionData } from '@/types/api';

export function BankTab() {
  const { data: connections, isLoading } = useBankConnections();

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <SkeletonList rows={3} />
        </CardBody>
      </Card>
    );
  }

  const hasAny = (connections ?? []).length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Linked banks</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Connect a bank to import transactions automatically. Each external account maps to one row in your Chart of accounts.
          </p>
        </div>
        <AddBankButton />
      </div>

      {!hasAny ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={Landmark}
              title="No banks connected yet"
              description="Link your first business checking account to start syncing transactions into the ledger."
              action={<AddBankButton />}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {(connections ?? []).map((c) => (
            <ConnectionCard key={c.id} connection={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function AddBankButton() {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const mintToken = useCreateLinkToken();
  const exchange = useCreateBankConnection();
  const toast = useToast();

  const onSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken) => {
      // react-plaid-link 5 types the token as nullable; without one there is
      // nothing to exchange, so treat it as a failed connection.
      if (!publicToken) {
        toast.error("Plaid didn't return a connection. Try again.");
        return;
      }
      try {
        const conn = await exchange.mutateAsync(publicToken);
        toast.success(
          `Connected ${conn.institution_name ?? 'bank'} — ${conn.accounts.length} account${
            conn.accounts.length === 1 ? '' : 's'
          }`,
        );
      } catch {
        toast.error("Couldn't save the connection. Try again.");
      } finally {
        setLinkToken(null);
      }
    },
    [exchange, toast],
  );

  // Plaid fires onExit both on a plain user cancel (error == null) and on a
  // real failure (e.g. INVALID_LINK_CUSTOMIZATION, institution errors). Surface
  // the reason so a failed link tells the user what went wrong instead of just
  // silently closing the modal.
  const onExit: PlaidLinkOnExit = useCallback(
    (error) => {
      if (error) {
        toast.error(
          error.display_message ||
            error.error_message ||
            `Plaid could not complete the link${error.error_code ? ` (${error.error_code})` : ''}.`,
        );
      }
      setLinkToken(null);
    },
    [toast],
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Open the Plaid Link UI as soon as the token mints + Plaid SDK is ready.
  if (linkToken && ready) {
    open();
  }

  async function start() {
    try {
      const token = await mintToken.mutateAsync();
      setLinkToken(token);
    } catch {
      toast.error(
        'Could not create a Plaid link token. Check that PLAID_CLIENT_ID and PLAID_SECRET are set in .env.',
      );
    }
  }

  const pending = mintToken.isPending || exchange.isPending;

  return (
    <Button onClick={start} disabled={pending}>
      {pending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
      {pending ? 'Linking…' : 'Add bank'}
    </Button>
  );
}

function ConnectionCard({ connection }: { connection: BankConnectionData }) {
  const remove = useDeleteBankConnection();
  const sync = useSyncConnection();
  const confirm = useConfirm();
  const toast = useToast();

  async function onSync() {
    try {
      const summary = await sync.mutateAsync(connection.id);
      const total = summary.added + summary.modified;
      toast.success(
        total === 0 && summary.removed === 0
          ? 'Already up to date'
          : `Synced ${summary.added} new, ${summary.modified} updated`,
      );
    } catch {
      toast.error("Couldn't sync transactions. Check the connection and try again.");
    }
  }

  async function onDisconnect() {
    const ok = await confirm({
      title: `Disconnect ${connection.institution_name ?? 'bank'}?`,
      body: 'This unlinks the bank from Plaid. Transactions already imported stay in the ledger. You can re-link later.',
      confirmLabel: 'Disconnect',
      tone: 'danger',
    });
    if (!ok) return;
    await remove.mutateAsync(connection.id);
    toast.success('Bank disconnected');
  }

  // Archived accounts are closed or superseded: they keep their transactions
  // but stop being part of the day-to-day picture, so they are not counted as
  // unmapped and they sort to the bottom.
  const accounts = [...connection.accounts].sort(
    (a, b) => Number(a.archived_at != null) - Number(b.archived_at != null),
  );
  const unmapped = connection.accounts.filter(
    (a) => a.posted_to_account_id == null && a.archived_at == null,
  ).length;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Landmark className="size-4 text-muted-foreground" />
            <h4 className="truncate text-sm font-semibold">
              {connection.institution_name ?? 'Linked bank'}
            </h4>
            <StatusBadge status={connection.status} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {connection.accounts.length} account{connection.accounts.length === 1 ? '' : 's'}
            {unmapped > 0
              ? ` · ${unmapped} not yet mapped to a ledger account`
              : ' · all mapped'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button variant="secondary" onClick={onSync} disabled={sync.isPending}>
            {sync.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            {sync.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button variant="secondary" onClick={onDisconnect}>
            <Unlink className="size-3.5" /> Disconnect
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <DataList>
          {accounts.map((a) => (
            <AccountRow key={a.id} account={a} />
          ))}
        </DataList>
      </CardBody>
    </Card>
  );
}

function AccountRow({ account }: { account: BankAccountData }) {
  const { data: postable, isLoading } = usePostableAccounts();
  const update = useUpdateBankAccount();
  const confirm = useConfirm();
  const toast = useToast();

  const linked = account.posted_to_account_id != null;
  const archived = account.archived_at != null;

  // A checking account posts to an asset, a credit card to a liability; the
  // server decides which via ledger_account_types and enforces it on save.
  const options = (postable ?? []).filter((p) => account.ledger_account_types.includes(p.type));
  // Keep the current mapping visible even when it is no longer offered (an
  // archived ledger row, say), so a mapped account never reads as unmapped.
  const currentHidden =
    linked && !isLoading && !options.some((p) => p.id === account.posted_to_account_id);

  async function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const raw = e.target.value;
    const id = raw === '' ? null : Number(raw);
    try {
      await update.mutateAsync({ id: account.id, posted_to_account_id: id });
      toast.success(linked ? 'Account mapping updated' : 'Account linked');
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  return (
    <DataListRow>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{account.name}</span>
          {account.mask ? (
            <span className="truncate text-xs text-muted-foreground">····{account.mask}</span>
          ) : null}
          <SubtypeBadge type={account.type} subtype={account.subtype} />
          {archived ? <Badge tone="neutral">Archived</Badge> : null}
        </div>
        {account.current_balance_cents != null ? (
          <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
            Balance ${(account.current_balance_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        ) : null}
      </div>

      <div className="flex w-72 shrink-0 items-center gap-2">
        {archived ? null : linked ? (
          <CheckCircle2 className="size-3.5 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <AlertTriangle className="size-3.5 text-amber-600 dark:text-amber-500" />
        )}
        <Select
          value={account.posted_to_account_id ?? ''}
          onChange={onChange}
          disabled={isLoading || archived}
          className="flex-1"
        >
          <option value="">{isLoading ? 'Loading…' : 'Pick a ledger account…'}</option>
          {currentHidden ? (
            <option value={account.posted_to_account_id ?? ''}>
              {account.posted_to_account_label ?? 'Current account'}
            </option>
          ) : null}
          {options.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </Select>
      </div>

      <Menu
        items={[
          archived
            ? {
                key: 'restore',
                label: 'Restore account',
                icon: RotateCcw,
                onSelect: async () => {
                  await update.mutateAsync({ id: account.id, archived: false });
                  toast.success(`${account.name} restored`);
                },
              }
            : {
                key: 'archive',
                label: 'Archive account',
                icon: Archive,
                onSelect: async () => {
                  const ok = await confirm({
                    title: `Archive ${account.name}?`,
                    body: 'Use this for an account that is closed. Its transactions stay in the ledger and keep explaining the entries posted from them; it just stops being offered as a transfer destination. Bank accounts are never deleted for that reason.',
                    confirmLabel: 'Archive account',
                  });
                  if (ok) {
                    await update.mutateAsync({ id: account.id, archived: true });
                    toast.success(`${account.name} archived`);
                  }
                },
              },
        ]}
      />
    </DataListRow>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge tone="success">Active</Badge>;
  if (status === 'error') return <Badge tone="warning">Needs attention</Badge>;
  if (status === 'revoked') return <Badge tone="danger">Revoked</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

function SubtypeBadge({ type, subtype }: { type: string; subtype: string | null }) {
  const label = subtype ?? type;
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
  );
}

